/**
 * TenantBackupsPage — Tenant-scoped Backup Status View
 *
 * Read-only view for tenant admins showing:
 * - Backup policy assignment (tier, schedule, retention)
 * - Backup history (last 10 backups with status/size/duration)
 * - Next scheduled run
 * - Backup health indicator
 *
 * WQ-TENANT-ADMIN-NAV: Part of the Administration nav group.
 * Uses existing backup_router endpoints (read-only, tenant_admin gated).
 * Write operations (trigger, delete, toggle) require super_admin.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAdminContext } from '../contexts/AdminContextProvider';
import {
  DatabaseIcon,
  RefreshIcon,
  CheckCircleIcon,
  AlertIcon,
  ClockIcon,
  ShieldIcon,
  ActivityIcon,
} from '../components/common/Icons';

// =============================================================================
// Types
// =============================================================================

interface BackupConfig {
  tenant_id: string;
  enabled: boolean;
  frequency: string;
  retention_count: number;
  retention_days: number;
  destination_type: string;
  scope: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

interface BackupHistoryItem {
  id: string;
  tenant_id: string;
  status: string;
  backup_type: string;
  size_bytes: number | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
}

interface BackupNextRun {
  tenant_id: string;
  enabled: boolean;
  frequency: string;
  last_backup_at: string | null;
  next_run_at: string | null;
  message: string;
}

// =============================================================================
// Helpers
// =============================================================================

function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(1)} ${units[i]}`;
}

function formatDuration(start: string | null, end: string | null): string {
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
}

function statusColor(status: string): string {
  switch (status) {
    case 'completed': return '#10b981';
    case 'failed': return '#ef4444';
    case 'running': case 'pending': return '#f59e0b';
    default: return 'var(--text-tertiary)';
  }
}

function frequencyLabel(freq: string): string {
  switch (freq) {
    case 'daily': return 'Daily';
    case 'every_12h': return 'Every 12 hours';
    case 'weekly': return 'Weekly';
    case 'monthly': return 'Monthly';
    default: return freq;
  }
}

// =============================================================================
// Component
// =============================================================================

const TenantBackupsPage: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const { activeTenant } = useAdminContext();

  const [tenantId, setTenantId] = useState<string | null>(activeTenant?.id || null);
  const [config, setConfig] = useState<BackupConfig | null>(null);
  const [history, setHistory] = useState<BackupHistoryItem[]>([]);
  const [nextRun, setNextRun] = useState<BackupNextRun | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Resolve tenant ID: activeTenant (super admin) or fetch from tenant portal
  useEffect(() => {
    if (authLoading) return;
    if (activeTenant?.id) {
      setTenantId(activeTenant.id);
      return;
    }
    // Tenant admin: fetch own tenant ID from portal settings
    (async () => {
      try {
        const resp = await authFetch('/api/tenant-portal/settings');
        if (resp.ok) {
          const data = await resp.json();
          setTenantId(data.tenant_id);
        }
      } catch {
        // Will show error in fetchData
      }
    })();
  }, [authLoading, activeTenant, authFetch]);

  const fetchData = useCallback(async () => {
    if (!tenantId) return;
    setIsLoading(true);
    setError(null);

    try {
      // Fetch all three in parallel
      const [configResp, historyResp, nextRunResp] = await Promise.all([
        authFetch(`/api/tenants/${tenantId}/backups/config`).catch(() => null),
        authFetch(`/api/tenants/${tenantId}/backups/?limit=10&offset=0`).catch(() => null),
        authFetch(`/api/tenants/${tenantId}/backups/next-run`).catch(() => null),
      ]);

      // Config (404 = no config yet, that's OK)
      if (configResp?.ok) {
        setConfig(await configResp.json());
      } else {
        setConfig(null);
      }

      // History
      if (historyResp?.ok) {
        const data = await historyResp.json();
        setHistory(data.items || []);
      } else {
        setHistory([]);
      }

      // Next run
      if (nextRunResp?.ok) {
        setNextRun(await nextRunResp.json());
      } else {
        setNextRun(null);
      }
    } catch (err) {
      console.error('[TenantBackupsPage] Error:', err);
      setError('Failed to load backup information');
    }

    setIsLoading(false);
  }, [tenantId, authFetch]);

  useEffect(() => {
    if (tenantId) fetchData();
  }, [tenantId, fetchData]);

  // Determine overall health
  const lastBackup = history[0];
  const isHealthy = config?.enabled && lastBackup?.status === 'completed';
  const isWarning = config?.enabled && (!lastBackup || lastBackup?.status === 'failed');
  const isInactive = !config?.enabled;

  if (isLoading && !config) {
    return (
      <div className="page-container" style={{ padding: '32px' }}>
        <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-tertiary)' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <p>Loading backup information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ padding: '24px', maxWidth: '960px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '4px' }}>Backup & Recovery</h1>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
            View your organization's backup configuration and history
          </p>
        </div>
        <button
          className="btn btn-ghost"
          onClick={fetchData}
          disabled={isLoading}
          title="Refresh"
        >
          <RefreshIcon size={18} />
        </button>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* Status Cards Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {/* Health Status */}
        <div className="glass-card" style={{ padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            {isHealthy ? (
              <CheckCircleIcon size={18} style={{ color: '#10b981' }} />
            ) : isWarning ? (
              <AlertIcon size={18} style={{ color: '#f59e0b' }} />
            ) : (
              <ShieldIcon size={18} style={{ color: 'var(--text-tertiary)' }} />
            )}
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Status</span>
          </div>
          <div style={{ fontSize: '18px', fontWeight: 600, color: isHealthy ? '#10b981' : isWarning ? '#f59e0b' : 'var(--text-tertiary)' }}>
            {isHealthy ? 'Healthy' : isWarning ? 'Attention Needed' : 'Not Configured'}
          </div>
        </div>

        {/* Schedule */}
        <div className="glass-card" style={{ padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <ClockIcon size={18} style={{ color: 'var(--text-secondary)' }} />
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Schedule</span>
          </div>
          <div style={{ fontSize: '18px', fontWeight: 600 }}>
            {config ? frequencyLabel(config.frequency) : '—'}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
            {config ? `${config.retention_count} copies, ${config.retention_days}d retention` : 'No policy assigned'}
          </div>
        </div>

        {/* Next Run */}
        <div className="glass-card" style={{ padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <ActivityIcon size={18} style={{ color: 'var(--text-secondary)' }} />
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Next Backup</span>
          </div>
          <div style={{ fontSize: '18px', fontWeight: 600 }}>
            {nextRun?.next_run_at ? formatDate(nextRun.next_run_at) : '—'}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
            {nextRun?.message || (isInactive ? 'Backups not enabled' : '')}
          </div>
        </div>
      </div>

      {/* Backup History */}
      <div className="glass-card" style={{ padding: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <DatabaseIcon size={18} />
          <h2 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>Recent Backups</h2>
          <span style={{
            fontSize: '12px',
            color: 'var(--text-tertiary)',
            background: 'rgba(255,255,255,0.05)',
            padding: '2px 8px',
            borderRadius: '4px',
          }}>
            {history.length} shown
          </span>
        </div>

        {history.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-tertiary)' }}>
            <DatabaseIcon size={32} style={{ marginBottom: '8px', opacity: 0.3 }} />
            <p>No backup history available</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-secondary)', fontWeight: 500, fontSize: '12px' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-secondary)', fontWeight: 500, fontSize: '12px' }}>Type</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-secondary)', fontWeight: 500, fontSize: '12px' }}>Started</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--text-secondary)', fontWeight: 500, fontSize: '12px' }}>Size</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--text-secondary)', fontWeight: 500, fontSize: '12px' }}>Duration</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item) => (
                <tr
                  key={item.id}
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                >
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: statusColor(item.status),
                    }}>
                      <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: statusColor(item.status),
                      }} />
                      {item.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                    {item.backup_type}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                    {formatDate(item.started_at)}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text-secondary)', fontSize: '13px' }}>
                    {formatBytes(item.size_bytes)}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text-secondary)', fontSize: '13px' }}>
                    {formatDuration(item.started_at, item.completed_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Info note for tenant admins */}
      <div style={{
        marginTop: '16px',
        padding: '12px 16px',
        background: 'rgba(139, 92, 246, 0.08)',
        borderRadius: '8px',
        border: '1px solid rgba(139, 92, 246, 0.15)',
        fontSize: '13px',
        color: 'var(--text-secondary)',
      }}>
        <strong>Note:</strong> Backup schedules and policies are managed by your platform administrator.
        Contact your admin to adjust backup frequency, retention, or to request a manual backup.
      </div>
    </div>
  );
};

export default TenantBackupsPage;
